import React, { ReactNode, useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/hooks/useAuthContext';
import { formatRole } from '@/lib/utils';
import { Navigation } from './Navigation';
import ImpersonationBanner from './ImpersonationBanner';
import { NotificationBell } from '@/components/NotificationBell';
import { useTabBadge } from '@/hooks/useTabBadge';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  useTabBadge();
  const auth = useAuthContext();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // 🎯 Easter egg: Click logo 5 times rapidly to spin it
  const [logoSpinning, setLogoSpinning] = useState(false);
  const clickTimestamps = useRef<number[]>([]);

  const handleLogoClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const now = Date.now();
    // Keep only clicks within the last 2 seconds
    clickTimestamps.current = clickTimestamps.current.filter((t) => now - t < 2000);
    clickTimestamps.current.push(now);

    if (clickTimestamps.current.length >= 5) {
      setLogoSpinning(true);
      clickTimestamps.current = [];
      // Reset after animation completes
      setTimeout(() => setLogoSpinning(false), 1000);
      console.log('%c🎯 SPIN! 🎯', 'font-size: 24px;');
    }
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Get token from auth context if available (mock auth has token, Entra doesn't expose it)
  const token = 'token' in auth ? auth.token : undefined;
  const { user, isAuthenticated, logout } = auth;

  // Impersonation state — available on mock auth context
  const isImpersonating = 'isImpersonating' in auth ? auth.isImpersonating : false;
  const impersonatedUser = 'impersonatedUser' in auth ? auth.impersonatedUser : null;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login even if API call fails
      navigate('/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      {/* Impersonation Banner — above the main header */}
      {isImpersonating && <ImpersonationBanner />}

      {/* Brand-black Header */}
      <header className="bg-[#111111]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link
                to="/"
                onClick={handleLogoClick}
                className={`flex items-center gap-1 transition-all duration-300 ${logoSpinning ? '' : 'hover:opacity-80'}`}
                style={logoSpinning ? { animation: 'spin360 1s ease-in-out' } : undefined}
              >
                <span className="text-white text-xl sm:text-2xl font-bold tracking-tight">OUTFRONT</span>
                <span className="text-outfront-purple text-xl sm:text-2xl font-bold">/</span>
                <span className="text-white text-xl sm:text-2xl font-semibold">Hold Requests</span>
              </Link>
            </div>

            {isAuthenticated && user && (
              <div className="flex items-center space-x-3 sm:space-x-4">
                <NotificationBell token={token || null} />
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-outfront-purple"
                  >
                    {/* Mobile: avatar initials */}
                    <span className="sm:hidden w-8 h-8 rounded-full bg-outfront-purple text-white flex items-center justify-center text-sm font-medium shrink-0">
                      {(() => {
                        const displayUser = isImpersonating && impersonatedUser ? impersonatedUser : user;
                        return `${displayUser.firstName?.[0] || ''}${displayUser.lastName?.[0] || ''}`;
                      })()}
                    </span>
                    {/* Desktop: full name and role */}
                    <div className="hidden sm:block text-left">
                      <p className="font-medium text-white">
                        {isImpersonating && impersonatedUser
                          ? `${impersonatedUser.firstName} ${impersonatedUser.lastName}`
                          : `${user.firstName} ${user.lastName}`}
                      </p>
                      <p className="text-neutral-400 text-xs">
                        {formatRole(isImpersonating && impersonatedUser ? impersonatedUser.role : user.role)}
                      </p>
                    </div>
                    <svg className="w-4 h-4 text-neutral-400 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg border border-neutral-200 z-50 py-1">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/settings');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50 transition-colors"
                      >
                        Settings
                      </button>
                      <div className="border-t border-neutral-100 my-1" />
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          handleLogout();
                        }}
                        disabled={isLoggingOut}
                        className="w-full text-left px-4 py-2 text-sm text-status-negative hover:bg-status-negative-bg transition-colors disabled:opacity-50"
                      >
                        {isLoggingOut ? 'Logging out...' : 'Logout'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {isAuthenticated && user && (
        <Navigation
          userRole={isImpersonating && impersonatedUser ? impersonatedUser.role : user.role}
        />
      )}

      <main className="flex-1 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {children}
      </main>

      <footer className="bg-[#111111] mt-auto">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-neutral-400">
            &copy; {new Date().getFullYear()} OUTFRONT Media. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
